/* eslint-disable @typescript-eslint/no-unused-vars, react-refresh/only-export-components */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Bot,
  Brain,
  CheckCircle2,
  Compass,
  Copy,
  DollarSign,
  ExternalLink,
  FileText,
  Lock,
  Maximize2,
  MessageSquare,
  Minimize2,
  Plane,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Terminal,
  UserCheck,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { MarkdownMessage } from '../Common/MarkdownMessage';
import { RealityCheckCopilotPanel } from './RealityCheckCopilotPanel';

export interface DkAssistantMemoryItem {
  id: string;
  category:
    | 'routing_preference'
    | 'priority_rule'
    | 'client_preference'
    | 'communication_guardrail'
    | 'workflow_instruction'
    | 'team_boundary'
    | 'case_truth'
    | 'general'
    | string;
  title: string;
  instruction_text: string;
  applies_to_staff: string[];
  times_applied: number;
  is_active: boolean;
}

export interface DkAssistantExecutedAction {
  ok: boolean;
  blocked_by_guardrail?: boolean;
  action_type: string;
  summary: string;
  task_number?: number;
  customer_number?: number;
  assignee_name?: string;
  memory_id?: string;
}

export interface DkAssistantSuggestedChip {
  label: string;
  prompt: string;
}

export interface DkAssistantChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  sender_name: string;
  content: string;
  executed_actions: DkAssistantExecutedAction[];
  suggested_actions: DkAssistantSuggestedChip[];
  show_stokes_copilot_actions?: boolean;
  show_cashflow_actions?: boolean;
  created_at: string;
}

export interface DkAssistantAccessControl {
  mode: 'daniel_only' | 'selected_staff';
  allowed_staff_names: string[];
  expose_consultation_briefs_to_hosts: boolean;
  auto_execute_safe_list_actions: boolean;
}

interface SelectedActorCatalog {
  selected_user_ids: string[];
  eligible_staff: Array<{ user_id: string; display_name: string; role: string }>;
}

export interface DkAssistantUnchargedFormItem {
  ticket_display_id: number | null;
  fd_ticket_id: number | null;
  subject: string;
  customer_number: number | null;
  customer_name: string;
  pnr_locator: string | null;
  created_at: string;
  staged_intent_status: string;
  staged_amount_gbp: number | null;
  card_brand: string;
  card_last4: string;
  status_reason: string;
  ucc_url: string;
  customer_url: string;
  pnr_url: string | null;
  terminal_url: string;
}

export interface DkAssistantOpenDecisionItem {
  decision_key: string;
  source_type: string;
  badge: string;
  title: string;
  ask: string;
  recommendation: string;
  choices: string[];
  recommended_index: number;
  ucc_url?: string | null;
  customer_url?: string | null;
}

export interface DkAssistantSharedBrainRuleItem {
  ref_code: string;
  title: string;
  category: string;
  plain_english_summary: string;
  updated_at: string;
}

export interface DkAssistantQuestionsQueue {
  uncharged_count: number;
  processed_count: number;
  open_decisions_count: number;
  shared_brain_rules_count: number;
  active_memories_count: number;
  uncharged_booking_forms: DkAssistantUnchargedFormItem[];
  open_decisions: DkAssistantOpenDecisionItem[];
  recent_shared_rules: DkAssistantSharedBrainRuleItem[];
}

interface AssistantContextReceipt {
  access_control: DkAssistantAccessControl;
  memories: DkAssistantMemoryItem[];
  questions_queue?: DkAssistantQuestionsQueue | null;
}

const assistantMemoryCategories = new Set([
  'routing_preference',
  'priority_rule',
  'client_preference',
  'communication_guardrail',
  'workflow_instruction',
  'team_boundary',
  'case_truth',
  'general',
  'delegation_rule',
  'sabre_professional_playbook',
]);
function isAssistantMemory(value: unknown): value is DkAssistantMemoryItem {
  if (!value || typeof value !== 'object') return false;
  const memory = value as Partial<DkAssistantMemoryItem>;
  return typeof memory.id === 'string' && memory.id.length > 0 &&
    typeof memory.category === 'string' && memory.category.trim().length > 0 &&
    typeof memory.title === 'string' && memory.title.trim().length > 0 &&
    typeof memory.instruction_text === 'string' && memory.instruction_text.trim().length > 0 &&
    Array.isArray(memory.applies_to_staff) && memory.applies_to_staff.every((name) => typeof name === 'string') &&
    typeof memory.times_applied === 'number' && Number.isInteger(memory.times_applied) && memory.times_applied >= 0 &&
    typeof memory.is_active === 'boolean';
}

function parseAssistantContext(data: unknown): AssistantContextReceipt | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as { access_control?: unknown; memories?: unknown; questions_queue?: unknown };
  if (!Array.isArray(value.memories) || !value.memories.every(isAssistantMemory)) return null;
  if (!value.access_control || typeof value.access_control !== 'object') return null;
  const accessControl = value.access_control as Partial<DkAssistantAccessControl>;
  if (!['daniel_only', 'selected_staff'].includes(accessControl.mode || '') ||
    !Array.isArray(accessControl.allowed_staff_names) ||
    !accessControl.allowed_staff_names.every((name) => typeof name === 'string') ||
    typeof accessControl.expose_consultation_briefs_to_hosts !== 'boolean' ||
    typeof accessControl.auto_execute_safe_list_actions !== 'boolean') return null;
  const qRaw = value.questions_queue && typeof value.questions_queue === 'object'
    ? (value.questions_queue as Partial<DkAssistantQuestionsQueue>)
    : null;
  const questionsQueue: DkAssistantQuestionsQueue | null = qRaw
    ? {
        uncharged_count: Number(qRaw.uncharged_count ?? 0),
        processed_count: Number(qRaw.processed_count ?? 0),
        open_decisions_count: Number(qRaw.open_decisions_count ?? 0),
        shared_brain_rules_count: Number(qRaw.shared_brain_rules_count ?? 0),
        active_memories_count: Number(qRaw.active_memories_count ?? 0),
        uncharged_booking_forms: Array.isArray(qRaw.uncharged_booking_forms)
          ? (qRaw.uncharged_booking_forms as DkAssistantUnchargedFormItem[])
          : [],
        open_decisions: Array.isArray(qRaw.open_decisions)
          ? (qRaw.open_decisions as DkAssistantOpenDecisionItem[])
          : [],
        recent_shared_rules: Array.isArray(qRaw.recent_shared_rules)
          ? (qRaw.recent_shared_rules as DkAssistantSharedBrainRuleItem[])
          : [],
      }
    : null;
  return {
    access_control: accessControl as DkAssistantAccessControl,
    memories: value.memories,
    questions_queue: questionsQueue,
  };
}

function parseSelectedActorCatalog(data: unknown): SelectedActorCatalog | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as Partial<SelectedActorCatalog>;
  if (!Array.isArray(value.selected_user_ids) || !value.selected_user_ids.every((id) => typeof id === 'string')) return null;
  if (!Array.isArray(value.eligible_staff) || !value.eligible_staff.every((staff) =>
    staff && typeof staff.user_id === 'string' && typeof staff.display_name === 'string' && typeof staff.role === 'string'
  )) return null;
  return value as SelectedActorCatalog;
}

function parseExecutedAction(value: unknown): DkAssistantExecutedAction | null {
  if (!value || typeof value !== 'object') return null;
  const action = value as Partial<DkAssistantExecutedAction> & { subtask_id?: unknown };
  if (typeof action.ok !== 'boolean' || typeof action.action_type !== 'string' || !action.action_type) return null;
  if (typeof action.summary !== 'string' || !action.summary) return null;
  if (action.ok && action.action_type === 'save_learned_memory' && typeof action.memory_id !== 'string') return null;
  if (action.ok && action.action_type === 'reassign_case_or_subtask' && typeof action.subtask_id !== 'string') return null;
  return action as DkAssistantExecutedAction;
}

function parsePlaybookReceipt(data: unknown, questionRef: string): { memory_id: string } | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as { ok?: unknown; entry_id?: unknown; memory_id?: unknown; question_ref?: unknown };
  if (value.ok !== true || value.question_ref !== questionRef) return null;
  if (typeof value.entry_id !== 'string' || !value.entry_id) return null;
  if (typeof value.memory_id !== 'string' || !value.memory_id) return null;
  return { memory_id: value.memory_id };
}

export const STOKES_EX_ATH_SABRE_CRYPTIC_BLOCK = `*** SYNTHETIC TRAINING EXAMPLE — DO NOT EXECUTE ***
AAA TEST
-NAME/TRAINING EXAMPLE
01D115JANLHRTEST¤XX100
WPRW`;

export interface StokesExAthSegmentRow {
  index: number;
  dateLabel: string;
  isoDate: string;
  flight: string;
  from: string;
  to: string;
  depArr: string;
  bookingClass: 'D' | 'A';
  cabinLabel: string;
  oldKmukpoStatus: string;
  sabreSell: string;
  tp: number;
  liveStatusDefault: string;
  recommendedCodeshareFlight: string;
  codeshareSabreSell: string;
  codeshareTp: number;
  codeshareBenefit: string;
  uiRuleStatus: string;
  fallbackNote?: string;
}

export const STOKES_EX_ATH_13_SEGMENTS: StokesExAthSegmentRow[] = [
  {
    index: 1, dateLabel: '15 Jan (synthetic)', isoDate: '2099-01-15', flight: 'XX 100', from: 'LHR', to: 'TST', depArr: '10:00–12:00',
    bookingClass: 'D', cabinLabel: 'Synthetic Business (D)', oldKmukpoStatus: 'Synthetic disruption',
    sabreSell: '01D115JANLHRTEST¤XX100', tp: 0, liveStatusDefault: 'Example only — run live check',
    recommendedCodeshareFlight: 'XX 100 (synthetic)', codeshareSabreSell: '01D115JANLHRTEST¤XX100', codeshareTp: 0,
    codeshareBenefit: 'Training comparison only', uiRuleStatus: 'Synthetic example',
  },
];

export interface CustomerCodeshareTargetSummary {
  customerName: string;
  customerNumber: number;
  programAndGoal: string;
  currentBaselinePoints: string;
  codeshareMaximisedPoints: string;
  keyCodeshareSwaps: string;
  sabreCheckCommand: string;
}

export const CUSTOMER_CODESHARE_MAXIMISER_TARGETS: CustomerCodeshareTargetSummary[] = [
  {
    customerName: 'Synthetic Traveller A', customerNumber: 9000001,
    programAndGoal: 'Synthetic loyalty target', currentBaselinePoints: 'Illustrative baseline only',
    codeshareMaximisedPoints: 'Illustrative comparison only',
    keyCodeshareSwaps: 'Training example: compare eligible marketing and operating carriers using live authorised data.',
    sabreCheckCommand: '*** SYNTHETIC — DO NOT EXECUTE ***',
  },
];

export interface WaitingRtwQuotePipelineRow {
  rank: number;
  customerName: string;
  customerNumber: number;
  uccId: number | null;
  pnrLocator: string | null;
  productLabel: string;
  estCashInGbp: number;
  estCashInDisplay: string;
  waitDuration: string;
  bottleneck: string;
  fastCloseAction: string;
  delegatePrompt: string;
  fivePointRecapSummary: string;
}

// This is an explicit training row. Live waiting quotes must come from their own authorised data source.
export const WAITING_RTW_QUOTES_PIPELINE: WaitingRtwQuotePipelineRow[] = [
  {
    rank: 1, customerName: 'Synthetic Traveller A', customerNumber: 9000001, uccId: null, pnrLocator: null,
    productLabel: 'Synthetic RTW quote', estCashInGbp: 0, estCashInDisplay: 'Synthetic example — no live amount',
    waitDuration: 'Training example', bottleneck: 'No live case is represented.',
    fastCloseAction: 'Open the live worklist before taking action.',
    delegatePrompt: 'Explain the synthetic quote workflow; do not create a live task.',
    fivePointRecapSummary: 'Synthetic training recap only. Use the authorised live record for operational facts.',
  },
];

export interface SystemNavigationGuideEntry {
  area: string;
  path: string;
  description: string;
  whatYouCanDoThere: string;
}

export const SYSTEM_NAVIGATION_DIRECTORY: SystemNavigationGuideEntry[] = [
  { area: 'Daily command list', path: '/ops/today', description: 'Current operational work and source-backed briefs.', whatYouCanDoThere: 'Review live work and delegate through confirmed actions.' },
  { area: '1-Click Charge Terminal & First Booking Forms', path: '/finance/terminal', description: 'Pre-filled Stripe UK (DK) & US (Technology) card charges and First Booking Form reconciliation.', whatYouCanDoThere: 'Review uncharged First Booking Forms and execute 1-click authorised charges.' },
  { area: 'RTW desk', path: '/rtw', description: 'Round-the-world itinerary planning and review.', whatYouCanDoThere: 'Build and review itineraries using authorised case data.' },
  { area: 'Paid — Not Ticketed queue', path: '/pnt', description: 'Current paid bookings awaiting issuance.', whatYouCanDoThere: 'Review the live chronological queue.' },
  { area: 'Clients waiting for a reply', path: '/ops/waiting', description: 'Current response queue.', whatYouCanDoThere: 'Review source-backed conversation context and assign follow-ups.' },
  { area: 'Delivery & Decision Queue', path: '/admin/delivery', description: 'Live decision cards, collision table, and Shared Brain status.', whatYouCanDoThere: 'Answer open decision cards and review active work.' },
  { area: 'Workforce routing', path: '/admin/workforce', description: 'Skill and subtask routing controls.', whatYouCanDoThere: 'Review delegation rules and sign-off gates.' },
];

const DEFAULT_STANDING_MEMORIES: DkAssistantMemoryItem[] = [];

const INITIAL_ASSISTANT_WELCOME: DkAssistantChatMessage = {
  id: 'welcome-msg',
  role: 'assistant',
  sender_name: 'DK Management Assistant',
  content: 'Use this drawer to review live, source-backed operational context (First Booking Forms, PNT Cash-Flow, Questions Waiting for Daniel, and Shared Brain Rules) and request staff actions. Every rule you teach here or in Antigravity is dual-persisted to the Shared Brain (`ops_ai_answers_and_reports_hub` + `dk_management_assistant_memories`).',
  executed_actions: [],
  suggested_actions: [
    { label: '💳 Uncharged First Booking Forms', prompt: 'Are there any First Booking Forms not yet processed?' },
    { label: '❓ Questions waiting for Daniel', prompt: 'What questions and decisions are waiting for me right now?' },
    { label: 'Show current priorities', prompt: 'Show my current source-backed priorities' },
    { label: '🧠 Shared Brain standing rules', prompt: 'Show shared brain standing rules and continuous learning status' },
    { label: 'Open system navigator', prompt: 'Where is the relevant workflow in the system?' },
  ],
  created_at: new Date().toISOString(),
};

function isStokesRelatedMessage(_text: string): boolean {
  return false;
}

function isCashFlowQuotesMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return lower.includes('cash') || lower.includes('waiting quote') || lower.includes('pipeline');
}

export function buildLocalFallbackReply(message: string): {
  reply: string;
  executed: DkAssistantExecutedAction[];
  learnedMemory?: DkAssistantMemoryItem;
  showStokesActions?: boolean;
  showCashflowActions?: boolean;
} {
  const lower = message.toLowerCase();
  const navigationMatch = SYSTEM_NAVIGATION_DIRECTORY.find((entry) =>
    lower.includes(entry.area.toLowerCase()),
  );
  const bookingFormHint =
    lower.includes('first booking') || lower.includes('booking form') || lower.includes('uncharged')
      ? ' For live First Booking Forms and staged card charges, check /finance/terminal or the Uncharged Booking Forms filter pill on /.'
      : '';
  const guidance = navigationMatch
    ? ` The relevant area is ${navigationMatch.path}: ${navigationMatch.description}`
    : bookingFormHint;
  return {
    reply: `The live assistant response was not confirmed. No action or memory save was confirmed.${guidance} Your request remains visible in this chat; check the live work board or memory list before retrying.`,
    executed: [],
    showStokesActions: false,
    showCashflowActions: false,
  };
}

export interface DkManagementAssistantDrawerProps {
  pageContext?: string;
  externalPromptRequest?: { prompt: string; customerNumber?: number; nonce: number } | null;
  onActionCompleted?: () => void;
  defaultOpen?: boolean;
  floatingLauncher?: boolean;
}

export const DkManagementAssistantDrawer: React.FC<DkManagementAssistantDrawerProps> = ({
  pageContext = '/ops/today',
  externalPromptRequest = null,
  onActionCompleted,
  defaultOpen = false,
  floatingLauncher = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<
    'chat' | 'stokes_sabre' | 'answers_hub' | 'cashflow_quotes' | 'memories' | 'access'
  >('chat');
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DkAssistantChatMessage[]>([INITIAL_ASSISTANT_WELCOME]);
  const [inputText, setInputText] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const sendingRef = useRef(false);
  const consumedPromptNonces = useRef(new Set<number>());
  const [memories, setMemories] = useState<DkAssistantMemoryItem[]>(DEFAULT_STANDING_MEMORIES);
  const [contextLoadError, setContextLoadError] = useState<string | null>(null);
  const [accessControlReady, setAccessControlReady] = useState(false);
  const accessControlReadyRef = useRef(false);
  const contextLoadIdRef = useRef(0);
  const [accessControl, setAccessControl] = useState<DkAssistantAccessControl>({
    mode: 'daniel_only',
    allowed_staff_names: ['Daniel Krupnik'],
    expose_consultation_briefs_to_hosts: true,
    auto_execute_safe_list_actions: true,
  });
  const [selectedActorCatalog, setSelectedActorCatalog] = useState<SelectedActorCatalog | null>(null);
  const [selectedActorDraft, setSelectedActorDraft] = useState<string[]>([]);
  const [selectedActorLoadError, setSelectedActorLoadError] = useState<string | null>(null);
  const selectedActorLoadIdRef = useRef(0);

  // Inline synthetic training state. It is never evidence of a provider check.
  const [sabreStatusBanner, setSabreStatusBanner] = useState<string | null>(null);
  const [copiedCryptic, setCopiedCryptic] = useState<boolean>(false);
  const [qf62UseJlAlternative, setQf62UseJlAlternative] = useState<boolean>(true);
  const [codeshareMaximiserEnabled, setCodeshareMaximiserEnabled] = useState<boolean>(true);
  const [sabrePlaybookInput, setSabrePlaybookInput] = useState<string>('');
  const [sabrePlaybookSavedNote, setSabrePlaybookSavedNote] = useState<string | null>(null);
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  const assistantMutationRef = useRef<string | null>(null);

  // New memory form state
  const [newMemoryTitle, setNewMemoryTitle] = useState<string>('');
  const [newMemoryCategory, setNewMemoryCategory] = useState<string>('workflow_instruction');
  const [newMemoryText, setNewMemoryText] = useState<string>('');
  const [newMemoryStaff, setNewMemoryStaff] = useState<string>('All');
  const [savingMemory, setSavingMemory] = useState<boolean>(false);
  const [memorySaveNotice, setMemorySaveNotice] = useState<string | null>(null);
  const [questionsQueue, setQuestionsQueue] = useState<DkAssistantQuestionsQueue | null>(null);
  const [answeringDecisionKey, setAnsweringDecisionKey] = useState<string | null>(null);
  const [decisionAnswerNote, setDecisionAnswerNote] = useState<string>('');

  // Pillar B: Global Cross-Client "Ask Propeller" Omnichannel Search & Live Brief state
  const [omnichannelQuery, setOmnichannelQuery] = useState<string>('');
  const [omnichannelSearching, setOmnichannelSearching] = useState<boolean>(false);
  const [omnichannelMatches, setOmnichannelMatches] = useState<
    Array<{
      customer_id: string;
      customer_number?: number | null;
      customer_name: string;
      customer_email?: string | null;
      customer_phone?: string | null;
      preferred_program?: string | null;
      ba_current_tier?: string | null;
      display_id?: number | null;
      fd_ticket_id?: number | null;
      subject?: string | null;
      primary_pnr?: string | null;
      match_channel?: string | null;
      match_snippet?: string | null;
      latest_activity_at?: string | null;
      staged_draft_status?: string | null;
      ucc_url?: string | null;
      crm_360_url?: string | null;
    }>
  >([]);
  const [omnichannelExcludedNames, setOmnichannelExcludedNames] = useState<string[]>([]);
  const [inlineBriefByClient, setInlineBriefByClient] = useState<Record<string, Record<string, unknown>>>({});
  const [loadingBriefForClient, setLoadingBriefForClient] = useState<string | null>(null);
  const [inlineReplyDraftByClient, setInlineReplyDraftByClient] = useState<Record<string, string>>({});
  const [inlineReplyStatusByClient, setInlineReplyStatusByClient] = useState<Record<string, string>>({});

  const runOmnichannelClientSearch = useCallback(async (rawQuery: string) => {
    const q = rawQuery.trim();
    if (!q) return;
    setOmnichannelQuery(q);
    setOmnichannelSearching(true);
    try {
      const { data, error } = await supabase.rpc(
        'search_omnichannel_client_memory' as never,
        { p_query: q, p_exclude_names: [] } as never
      );
      if (!error && data && typeof data === 'object') {
        const res = data as {
          matches?: typeof omnichannelMatches;
          excluded_names?: string[];
        };
        setOmnichannelMatches(Array.isArray(res.matches) ? res.matches : []);
        setOmnichannelExcludedNames(Array.isArray(res.excluded_names) ? res.excluded_names : []);
      }
    } catch {
      // Ignore in offline/mocked tests
    } finally {
      setOmnichannelSearching(false);
    }
  }, []);

  const loadInlineClientBrief = useCallback(async (key: string) => {
    if (!key) return;
    setLoadingBriefForClient(key);
    try {
      const { data, error } = await supabase.rpc(
        'get_unified_client_decision_brief' as never,
        { p_ticket_or_pnr: key } as never
      );
      if (!error && data && typeof data === 'object') {
        setInlineBriefByClient((prev) => ({ ...prev, [key]: data as Record<string, unknown> }));
        const drafts = (data as { staged_drafts?: Array<{ draft_text?: string }> }).staged_drafts;
        if (Array.isArray(drafts) && drafts[0]?.draft_text) {
          setInlineReplyDraftByClient((prev) => ({ ...prev, [key]: drafts[0].draft_text || '' }));
        }
      }
    } catch {
      // Ignore in offline/mocked tests
    } finally {
      setLoadingBriefForClient(null);
    }
  }, []);

  // Global Cmd/Ctrl + J hotkey & custom event listener so "Ask Propeller AI" opens from any screen
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        setActiveTab('chat');
      }
    };
    const onOpenEvent = () => {
      setIsOpen(true);
      setActiveTab('chat');
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('open-global-propeller-ai', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('open-global-propeller-ai', onOpenEvent);
    };
  }, []);

  const loadContext = useCallback(async (): Promise<AssistantContextReceipt | null> => {
    const loadId = ++contextLoadIdRef.current;
    accessControlReadyRef.current = false;
    setAccessControlReady(false);
    setContextLoadError(null);
    try {
      const { data, error } = await supabase.rpc(
        'dk_management_assistant_context_v1' as never,
        { p_query: null } as never
      );
      if (error) throw error;
      const ctx = parseAssistantContext(data);
      if (!ctx) throw new Error('Assistant context did not return a complete receipt');
      if (loadId !== contextLoadIdRef.current) return null;
      setMemories(ctx.memories);
      if (ctx.questions_queue) {
        setQuestionsQueue(ctx.questions_queue);
      }
      if (ctx.access_control && ['daniel_only', 'selected_staff'].includes(ctx.access_control.mode)) {
        setAccessControl((prev) => ({ ...prev, ...ctx.access_control }));
        accessControlReadyRef.current = true;
        setAccessControlReady(true);
      }
      return ctx;
    } catch {
      if (loadId === contextLoadIdRef.current) {
        setMemories([]);
        setContextLoadError('Live assistant context unavailable. No example memories are shown as saved records.');
      }
      return null;
    }
  }, []);

  const handleAnswerDecision = useCallback(
    async (decisionKey: string, choiceText: string, noteText?: string) => {
      if (!decisionKey || !choiceText.trim() || answeringDecisionKey) return;
      setAnsweringDecisionKey(decisionKey);
      try {
        const { data, error } = await supabase.rpc(
          'dk_management_assistant_answer_question_v1' as never,
          {
            p_decision_key: decisionKey,
            p_choice: choiceText.trim(),
            p_note: noteText?.trim() || null,
          } as never
        );
        if (error) throw error;
        const receipt = data as { ok?: boolean; summary?: string } | null;
        if (receipt?.ok === true) {
          setDecisionAnswerNote(receipt.summary || `Recorded decision for ${decisionKey}: ${choiceText}`);
          await loadContext();
        }
      } catch {
        setDecisionAnswerNote('Could not confirm decision save; please retry or use chat.');
      } finally {
        setAnsweringDecisionKey(null);
      }
    },
    [answeringDecisionKey, loadContext]
  );

  useEffect(() => {
    void loadContext();
    return () => { contextLoadIdRef.current += 1; accessControlReadyRef.current = false; };
  }, [loadContext]);

  const loadSelectedActors = useCallback(async (): Promise<SelectedActorCatalog | null> => {
    const loadId = ++selectedActorLoadIdRef.current;
    setSelectedActorCatalog(null);
    setSelectedActorLoadError(null);
    try {
      const { data, error } = await supabase.rpc('dk_management_assistant_selected_actors_v1' as never);
      if (error) throw error;
      const catalog = parseSelectedActorCatalog(data);
      if (!catalog) throw new Error('Selected actor list was incomplete');
      if (loadId !== selectedActorLoadIdRef.current) return null;
      setSelectedActorCatalog(catalog);
      const eligibleIds = new Set(catalog.eligible_staff.map((staff) => staff.user_id));
      setSelectedActorDraft(catalog.selected_user_ids.filter((id) => eligibleIds.has(id)));
      return catalog;
    } catch {
      if (loadId === selectedActorLoadIdRef.current)
        setSelectedActorLoadError('Selected staff IDs are unavailable or you cannot manage them. Access cannot be changed here.');
      return null;
    }
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === 'access') void loadSelectedActors();
    return () => { selectedActorLoadIdRef.current += 1; };
  }, [isOpen, activeTab, loadSelectedActors]);

  const sendMessage = useCallback(
    async (rawText: string) => {
      const trimmed = rawText.trim();
      if (!trimmed || sendingRef.current) return;
      sendingRef.current = true;

      const userMsg: DkAssistantChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        sender_name: 'Daniel Krupnik',
        content: trimmed,
        executed_actions: [],
        suggested_actions: [],
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInputText('');
      setSending(true);

      const stokesQuery = isStokesRelatedMessage(trimmed);
      const cashflowQuery = isCashFlowQuotesMessage(trimmed);

      try {
        const { data, error } = await supabase.rpc(
          'dk_management_assistant_chat_v1' as never,
          {
            p_thread_id: threadId,
            p_message: trimmed,
            p_auto_execute: true,
          } as never
        );

        if (!error && data && typeof data === 'object' && (data as { ok?: boolean }).ok === true) {
          const res = data as {
            thread_id?: string;
            assistant_message_id?: string;
            reply?: string;
            actions?: Array<Record<string, unknown>>;
            executed_actions?: DkAssistantExecutedAction[];
            suggested_actions?: DkAssistantSuggestedChip[];
            suggested_prompts?: string[];
            learned_memory_ids?: string[];
          };
          if (!res.thread_id || !res.assistant_message_id || typeof res.reply !== 'string' || !res.reply.trim()) {
            throw new Error('Assistant response did not return a complete receipt');
          }
          if (res.thread_id) setThreadId(res.thread_id);

          const actionSource = Array.isArray(res.executed_actions) ? res.executed_actions : res.actions;
          const mappedActions = Array.isArray(actionSource)
            ? actionSource.map(parseExecutedAction).filter((action): action is DkAssistantExecutedAction => action !== null)
            : [];
          if (Array.isArray(actionSource) && mappedActions.length !== actionSource.length) {
            throw new Error('Assistant action receipt was incomplete');
          }
          const learnedIds = Array.isArray(res.learned_memory_ids)
            ? res.learned_memory_ids.filter((id): id is string => typeof id === 'string' && Boolean(id))
            : [];
          if (learnedIds.length > 0) {
            const context = await loadContext();
            if (!context || learnedIds.some((id) => !context.memories.some((memory) => memory.id === id))) {
              throw new Error('Learned memory could not be read back');
            }
          }

          const mappedChips: DkAssistantSuggestedChip[] = Array.isArray(res.suggested_actions)
            ? res.suggested_actions
            : Array.isArray(res.suggested_prompts)
              ? res.suggested_prompts.map((p) => ({ label: p, prompt: p }))
              : INITIAL_ASSISTANT_WELCOME.suggested_actions;

          const assistantMsg: DkAssistantChatMessage = {
            id: res.assistant_message_id || `asst-${Date.now()}`,
            role: 'assistant',
            sender_name: 'DK Unified Command & Sabre Co-Pilot',
            content: mappedActions.some((action) => !action.ok || action.blocked_by_guardrail)
              ? `Some requested actions were not completed. Review the individual action results below.${learnedIds.length > 0 ? ' The returned memory records were verified separately.' : ' No new memory save was confirmed.'}`
              : res.reply,
            executed_actions: mappedActions,
            suggested_actions: mappedChips,
            show_stokes_copilot_actions: stokesQuery || /9000001|900001|DEMOAA|ex-ATH/i.test(res.reply || ''),
            show_cashflow_actions: cashflowQuery || /Cash-Flow|Waiting RTW Quotes/i.test(res.reply || ''),
            created_at: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, assistantMsg]);

          if (assistantMsg.executed_actions.some((action) => action.ok && !action.blocked_by_guardrail)) {
            if (learnedIds.length === 0) void loadContext();
            onActionCompleted?.();
          }
          sendingRef.current = false;
          setSending(false);
          return;
        }
      } catch {
        // Render an explicit unconfirmed response below.
      }

      const fallback = buildLocalFallbackReply(trimmed);
      const fallbackMsg: DkAssistantChatMessage = {
        id: `asst-local-${Date.now()}`,
        role: 'assistant',
        sender_name: 'DK Unified Command & Sabre Co-Pilot',
        content: fallback.reply,
        executed_actions: fallback.executed,
        suggested_actions: INITIAL_ASSISTANT_WELCOME.suggested_actions,
        show_stokes_copilot_actions: fallback.showStokesActions ?? stokesQuery,
        show_cashflow_actions: fallback.showCashflowActions ?? cashflowQuery,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, fallbackMsg]);
      sendingRef.current = false;
      setSending(false);
    },
    [threadId, loadContext, onActionCompleted]
  );

  // Handle external prompt trigger from Consultation Briefs Panel or Task Board
  useEffect(() => {
    if (externalPromptRequest?.prompt.trim() && !sendingRef.current && !consumedPromptNonces.current.has(externalPromptRequest.nonce)) {
      // Consume before dispatch: thread-id and parent callback changes rerun this effect.
      // An uncertain response must not automatically replay an executable prompt.
      consumedPromptNonces.current.add(externalPromptRequest.nonce);
      setIsOpen(true);
      setActiveTab('chat');
      void sendMessage(externalPromptRequest.prompt);
    }
  }, [externalPromptRequest, sendMessage, sending]);

  const handleCheckLiveSabreAth = useCallback(() => {
    setIsOpen(true);
    setSabreStatusBanner(
      'Synthetic training scenario only. No provider check was run and no inventory is confirmed. Open an authorised live PNR before checking availability or acting.'
    );
  }, []);

  const handleCopySabreBlock = useCallback(async () => {
    const blockToCopy = qf62UseJlAlternative
      ? STOKES_EX_ATH_SABRE_CRYPTIC_BLOCK.replace(
          '02D104DECNRTBNE¤QF62',
          '02D104DECNRTSYD¤JL771\n02D105DECSYDBNE¤QF512'
        )
      : STOKES_EX_ATH_SABRE_CRYPTIC_BLOCK;
    try {
      await navigator.clipboard.writeText(blockToCopy);
      setCopiedCryptic(true);
      setSabreStatusBanner(
        'Copied synthetic training block. Do not paste or execute it against a live record.'
      );
      setTimeout(() => setCopiedCryptic(false), 4000);
    } catch {
      setCopiedCryptic(true);
      setSabreStatusBanner('Synthetic training block is displayed inline. No live record was changed.');
    }
  }, [qf62UseJlAlternative]);

  const handleDelegateStokesToOperator = useCallback(() => {
    setIsOpen(true);
    setSabreStatusBanner(
      'Synthetic training scenario only. No operator task was created. Start delegation from an authorised live customer record.'
    );
  }, []);

  const handleSaveSabrePlaybookAnswer = useCallback(
    async (answerText: string) => {
      const cleaned = answerText.trim();
      if (!cleaned || assistantMutationRef.current) return;
      const questionRef = `[DK-PLAYBOOK-${Date.now()}]`;
      assistantMutationRef.current = 'playbook';
      setSavingPlaybook(true);
      setSabrePlaybookSavedNote(null);
      try {
        const { data, error } = await supabase.rpc('ops_save_sabre_playbook_answer_v1' as never, {
          p_question_ref: questionRef,
          p_answer_text: cleaned,
          p_scenario_class: 'sabre_professional_playbook',
          p_applies_to_customer_number: null,
        } as never);
        if (error) throw error;
        const receipt = parsePlaybookReceipt(data, questionRef);
        if (!receipt) throw new Error('Playbook save did not return a complete receipt');
        const context = await loadContext();
        const saved = context?.memories.find((memory) => memory.id === receipt.memory_id);
        if (!saved || saved.instruction_text !== cleaned) throw new Error('Playbook rule could not be read back');
        setSabrePlaybookInput('');
        setSabrePlaybookSavedNote(`Saved and read back: "${cleaned}"`);
      } catch {
        setSabrePlaybookSavedNote('Save not confirmed. Your rule is retained; check the playbook before trying again.');
      } finally {
        assistantMutationRef.current = null;
        setSavingPlaybook(false);
      }
    },
    [loadContext]
  );

  const handleSaveNewMemory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemoryText.trim() || assistantMutationRef.current) return;
    assistantMutationRef.current = 'new-memory';
    setSavingMemory(true);
    setMemorySaveNotice(null);
    const titleToUse = newMemoryTitle.trim() || newMemoryText.trim().slice(0, 70);
    const staffArr =
      newMemoryStaff === 'All'
        ? ['All']
        : newMemoryStaff
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

    try {
      const instructionText = newMemoryText.trim();
      const { data, error } = await supabase.rpc(
        'dk_management_assistant_execute_action_v1' as never,
        {
          p_action_type: 'save_learned_memory',
          p_payload: {
            category: newMemoryCategory,
            title: titleToUse,
            instruction_text: instructionText,
            applies_to_staff: staffArr,
            source: 'admin_ui',
          },
        } as never
      );
      if (error) throw error;
      const receipt = parseExecutedAction(data);
      if (!receipt || receipt.ok !== true || receipt.action_type !== 'save_learned_memory' || !receipt.memory_id) {
        throw new Error('Memory save did not return a complete receipt');
      }
      const context = await loadContext();
      const saved = context?.memories.find((memory) => memory.id === receipt.memory_id);
      if (!saved || saved.instruction_text !== instructionText) throw new Error('Memory could not be read back');
      setNewMemoryTitle('');
      setNewMemoryText('');
      setMemorySaveNotice('Memory saved and read back.');
    } catch {
      setMemorySaveNotice('Memory save not confirmed. Your title and instruction are retained.');
    } finally {
      assistantMutationRef.current = null;
      setSavingMemory(false);
    }
  };

  const handleToggleMemory = async (mem: DkAssistantMemoryItem) => {
    if (assistantMutationRef.current) return;
    assistantMutationRef.current = `toggle:${mem.id}`;
    const nextState = !mem.is_active;
    setMemorySaveNotice(null);
    try {
      const { data, error } = await supabase.rpc(
        'dk_management_assistant_execute_action_v1' as never,
        {
          p_action_type: 'toggle_memory_active',
          p_payload: { memory_id: mem.id, is_active: nextState },
        } as never
      );
      if (error) throw error;
      const receipt = parseExecutedAction(data);
      if (!receipt || receipt.ok !== true || receipt.action_type !== 'toggle_memory_active' || receipt.memory_id !== mem.id) throw new Error('Toggle receipt was incomplete');
      const context = await loadContext();
      const saved = context?.memories.find((memory) => memory.id === mem.id);
      if (!saved || saved.is_active !== nextState) throw new Error('Toggle could not be read back');
      setMemorySaveNotice('Memory status saved and read back.');
    } catch {
      setMemorySaveNotice('Memory status change not confirmed. The displayed server state was retained.');
    } finally {
      assistantMutationRef.current = null;
    }
  };

  const handleUpdateAccessMode = async (mode: 'daniel_only' | 'selected_staff') => {
    if (assistantMutationRef.current || !accessControlReadyRef.current || !selectedActorCatalog ||
      (mode === 'selected_staff' && !hasEligibleSelectedActor)) return;
    assistantMutationRef.current = `access:${mode}`;
    setMemorySaveNotice(null);
    try {
      const { data, error } = await supabase.rpc(
        'dk_management_assistant_execute_action_v1' as never,
        {
          p_action_type: 'update_access_settings',
          p_payload: { mode },
        } as never
      );
      const receipt = parseExecutedAction(data);
      if (error || !receipt || receipt.ok !== true || receipt.action_type !== 'update_access_settings') throw error || new Error('Access receipt was incomplete');
      const context = await loadContext();
      if (!context || context.access_control?.mode !== mode) throw new Error('Access setting could not be read back');
      setMemorySaveNotice('Access setting saved and read back.');
    } catch {
      setMemorySaveNotice('Access setting not confirmed. The displayed server state was retained.');
    } finally {
      assistantMutationRef.current = null;
    }
  };

  const handleSaveSelectedActors = async () => {
    if (assistantMutationRef.current || !selectedActorCatalog) return;
    assistantMutationRef.current = 'selected-actor-ids';
    setMemorySaveNotice(null);
    try {
      const { data, error } = await supabase.rpc(
        'dk_management_assistant_set_selected_actors_v1' as never,
        { p_user_ids: selectedActorDraft } as never
      );
      const receipt = data as { ok?: unknown; selected_user_ids?: unknown } | null;
      if (error || receipt?.ok !== true || !Array.isArray(receipt.selected_user_ids)) throw error || new Error('Selected actor receipt was incomplete');
      const catalog = await loadSelectedActors();
      if (!catalog || JSON.stringify([...catalog.selected_user_ids].sort()) !== JSON.stringify([...selectedActorDraft].sort())) {
        throw new Error('Selected actor IDs could not be read back');
      }
      setMemorySaveNotice('Selected staff IDs saved and read back. Access mode was not changed.');
    } catch {
      setMemorySaveNotice('Selected staff IDs were not confirmed. Reload the access list before another change.');
    } finally {
      assistantMutationRef.current = null;
    }
  };

  const activeMemoriesCount = memories.filter((m) => m.is_active).length;
  const hasEligibleSelectedActor = selectedActorCatalog?.selected_user_ids.some((id) =>
    selectedActorCatalog.eligible_staff.some((staff) => staff.user_id === id)) ?? false;

  const renderStokesFourActionButtons = (compact = false) => (
    <div
      data-testid="stokes-copilot-4-action-buttons"
      className={`rounded-xl border border-purple-300 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 p-3 space-y-2 ${
        compact ? 'mt-2' : 'mt-3'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-purple-950 flex items-center gap-1.5">
          <Terminal className="h-3.5 w-3.5 text-purple-700" />
          Synthetic training scenario — no live customer, PNR or inventory
        </span>
        <span className="rounded-full border border-purple-300 bg-white px-2 py-0.5 text-[10px] font-bold text-purple-900">
          13 Legs ex-ATH · 20,464 BA TP/pax · illustrative amount Paid
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          data-testid="copilot-btn-check-sabre-ath"
          onClick={() => void handleCheckLiveSabreAth()}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-700 hover:bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-2xs"
        >
          <Plane className="h-3.5 w-3.5 shrink-0" />
          <span>
            1. Live checks require an authorised PNR
          </span>
        </button>

        <button
          type="button"
          data-testid="copilot-btn-open-rtw-900001"
          onClick={() => window.location.assign('/rtw')}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-indigo-600 bg-indigo-700 hover:bg-indigo-600 px-3 py-2 text-xs font-bold text-white shadow-2xs"
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          <span>2. Open RTW workbench</span>
        </button>

        <button
          type="button"
          data-testid="copilot-btn-copy-sabre-block"
          onClick={() => void handleCopySabreBlock()}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 px-3 py-2 text-xs font-bold text-amber-200 shadow-2xs"
        >
          <Copy className="h-3.5 w-3.5 shrink-0" />
          <span>
            {copiedCryptic
              ? 'Copied synthetic training block'
              : '3. Copy synthetic training block'}
          </span>
        </button>

        <button
          type="button"
          data-testid="copilot-btn-delegate-operator"
          onClick={() => void handleDelegateStokesToOperator()}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-purple-700 bg-purple-700 hover:bg-purple-600 px-3 py-2 text-xs font-bold text-white shadow-2xs"
        >
          <Zap className="h-3.5 w-3.5 shrink-0" />
          <span>4. Explain live-record delegation requirement</span>
        </button>
      </div>

      {sabreStatusBanner && (
        <div
          role="status"
          data-testid="stokes-sabre-live-status-banner"
          className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-950 flex flex-wrap items-center justify-between gap-2"
        >
          <span>{sabreStatusBanner}</span>
          <button
            type="button"
            onClick={() => setActiveTab('stokes_sabre')}
            className="rounded border border-emerald-400 bg-white px-2 py-0.5 text-[11px] font-bold text-emerald-900 hover:bg-emerald-100"
          >
            View Full 13-Leg Table &amp; Codeshare Maximiser Inline →
          </button>
        </div>
      )}

      {/* 🎓 Sabre Professional Question for Daniel (Peer-Level GDS Method — Teach Me Your Playbook) */}
      <div
        data-testid="sabre-professional-question-card"
        className="rounded-lg border border-indigo-300 bg-white/95 p-3 space-y-2 text-xs"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-extrabold text-indigo-950 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            🎓 Sabre Professional Question for Daniel (Peer-Level GDS Method — Teach Me Your Playbook)
          </span>
          <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-900">
            Saves to Layer 1 Sabre Playbook Memory
          </span>
        </div>
        <p className="text-slate-700 leading-relaxed">
          <strong>Sabre GDS Question (`BN5K via DW9L` · ex-`ATH` `DONE5` + Codeshare Maximisation)</strong>: On `QR 204 ATH-DOH` + `QR 1148 DOH-MCT` (24 Oct), QR enforces married O&amp;D availability (`124OCTATHMCT‡QR`), while swapping `DOH-BKK` (`BA*6241`), `HKG-NRT` (`JL*7050`), `NRT-SYD-BNE` (`JL 771 + BA*7412`), `BNE-MEL` (`BA*7385`), `MEL-LAX` (`AA*7349`), and `LAX-MIA-EZE` (`BA*1529`/`BA*4907`) boosts BA Qualifying Flights from 2 → 6 and Tier Points from 13,822 → <strong>20,464 TP/pax (+6,642 TP — Clears 20,000 BA Gold Target!)</strong> under Rule 3015 `¥UI*`. How do you want me and the operator to execute this in Sabre?
        </p>

        <div className="flex flex-wrap gap-1.5">
          {[
            'Sell QR Gulf legs as married O&D (124OCTATHMCT‡QR -> 02D1A2*), swap long-haul to BA*/AA*/JL* codeshares for 20,464 TP, and plate WPRW¥AQR for lowest YQ',
            'Swap HL Leg #7 (QF 62) to confirmed 02D104DECNRTSYD¤JL771 + 02D105DECSYDBNE¤BA7412 (+2,057 extra BA TP + BA Qualifying Flight) & store WPPQ',
            'Always check both prime & BA*/AA*/AY* codeshare D/A buckets in BN5K and pick the marketing code that hits the client tier target with lowest YQ',
          ].map((chipText, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => void handleSaveSabrePlaybookAnswer(chipText)}
              className="rounded-md border border-indigo-200 bg-indigo-50/90 hover:bg-indigo-100 px-2.5 py-1 text-left text-[11px] font-semibold text-indigo-950 transition"
            >
              ⚡ {chipText}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            type="text"
            data-testid="sabre-pro-playbook-input"
            value={sabrePlaybookInput}
            onChange={(e) => setSabrePlaybookInput(e.target.value)}
            placeholder='Explain your Sabre method (e.g. "In BN5K always pull married O&D with 124OCTATHMCT‡QR first, then X2/02BA* for codeshare TP, then WPRW¥AQR")…'
            className="flex-1 min-w-[240px] rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900"
          />
          <button
            type="button"
            data-testid="sabre-pro-playbook-save-btn"
            onClick={() => void handleSaveSabrePlaybookAnswer(sabrePlaybookInput)}
            disabled={!sabrePlaybookInput.trim() || savingPlaybook}
            className="rounded-md bg-indigo-900 hover:bg-indigo-800 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
          >
            {savingPlaybook ? 'Saving and checking…' : 'Save to Sabre Professional Playbook'}
          </button>
        </div>

        {sabrePlaybookSavedNote && (
          <div role="status" className="rounded border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[11px] font-bold text-slate-900">
            {sabrePlaybookSavedNote}
          </div>
        )}
      </div>
    </div>
  );

  // If mounted as global floating launcher on non-/ops/today pages and currently closed:
  if (floatingLauncher && !isOpen) {
    return (
      <div className="fixed bottom-4 right-20 z-50 flex flex-col items-end gap-2">
        <button
          type="button"
          data-testid="global-floating-ai-copilot-launcher"
          onClick={() => setIsOpen(true)}
          aria-label="Open Ask Propeller AI Global Master Co-Pilot"
          title="Ask Propeller AI — Global Master AI Co-Pilot (Ctrl/Cmd + J)"
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-blue-700 bg-propeller-blue text-white shadow-lg transition hover:bg-propeller-darkBlue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:h-auto sm:w-auto sm:gap-2 sm:px-3 sm:py-2.5"
        >
          <Bot className="h-5 w-5" aria-hidden="true" />
          <span className="hidden text-xs font-bold sm:inline">Ask Propeller AI</span>
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="dk-management-assistant-container"
      className={
        isFullScreen
          ? 'fixed inset-3 z-50 overflow-y-auto rounded-2xl border-2 border-indigo-500 bg-white p-3 shadow-2xl'
          : floatingLauncher
            ? 'fixed bottom-4 right-4 z-50 w-[96vw] max-w-5xl max-h-[88vh] overflow-y-auto rounded-2xl border-2 border-indigo-500 bg-white p-2 shadow-2xl'
            : 'my-3'
      }
    >
      {/* Embedded Command Strip (Always Visible on Daniel's List / Tasks / Workforce) */}
      <div className="rounded-lg border border-blue-200 bg-propeller-darkBlue p-3 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/20 border border-indigo-400/40">
              <Bot className="h-5 w-5 text-indigo-300" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-white">
                  DK Unified Command, Sabre PNR Re-Creation &amp; Cash-Flow Co-Pilot
                </span>
                <span
                  data-testid="dk-assistant-model-badge"
                  className="inline-flex items-center gap-1 rounded border border-blue-200/40 bg-propeller-blue px-2 py-0.5 text-[11px] font-semibold text-white"
                >
                  <Sparkles className="h-3 w-3 text-amber-300" aria-hidden="true" />
                  Server-backed actions · provider not reported
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 px-2 py-0.5 text-[11px] font-semibold text-emerald-200">
                  <Brain className="h-3 w-3" aria-hidden="true" />
                  4-Layer Knowledge ({activeMemoriesCount} Active Standing Memories)
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 px-2 py-0.5 text-[11px] font-medium text-slate-300">
                  <Lock className="h-3 w-3 text-amber-300" aria-hidden="true" />
                  {!accessControlReady ? 'Team Access: Unverified' : accessControl.mode === 'daniel_only'
                    ? 'Team Access: Daniel Only'
                    : 'Team Access: Selected Staff Enabled'}
                </span>
              </div>
              <p className="text-xs text-indigo-200/80 mt-0.5">
                Review current work, record verified case notes and open the relevant workflow. Training examples are labelled; actions require a confirmed server result.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-requirements-audit"
              onClick={() => {
                setIsOpen(true);
                void sendMessage('Audit current PNR and active cases against client WhatsApp, emails, and intake requirements');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-400/70 bg-indigo-600/90 hover:bg-indigo-500 px-3 py-1.5 text-xs font-extrabold text-white shadow-2xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              🔍 Audit PNR vs Client Requirements
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-booking-forms"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('chat');
                void sendMessage('Are there any First Booking Forms not yet processed?');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-400/80 bg-rose-600 hover:bg-rose-500 px-3 py-1.5 text-xs font-extrabold text-white shadow-2xs"
            >
              <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
              💳 Uncharged First Booking Forms
              {questionsQueue && questionsQueue.uncharged_count > 0 ? ` (${questionsQueue.uncharged_count})` : ''}
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-questions-waiting"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('chat');
                void sendMessage('What questions and decisions are waiting for me right now?');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/80 bg-amber-500 hover:bg-amber-400 px-3 py-1.5 text-xs font-extrabold text-slate-950 shadow-2xs"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              ❓ Questions Waiting for Daniel
              {questionsQueue && questionsQueue.open_decisions_count > 0 ? ` (${questionsQueue.open_decisions_count})` : ''}
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-proposal-pdf"
              onClick={() => {
                setIsOpen(true);
                void sendMessage('Generate clean client RTW proposal PDF summary and routing explanation');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/70 bg-emerald-600/90 hover:bg-emerald-500 px-3 py-1.5 text-xs font-extrabold text-white shadow-2xs"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              📄 Generate Proposal PDF Summary
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-stokes"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('stokes_sabre');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-400/70 bg-indigo-700/90 hover:bg-indigo-600 px-3 py-1.5 text-xs font-extrabold text-white shadow-2xs"
            >
              <Plane className="h-3.5 w-3.5" aria-hidden="true" />
              ✈️ Sabre Playbook &amp; Codeshare Lab
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-cashflow"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('cashflow_quotes');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400/70 bg-amber-500/90 hover:bg-amber-400 px-3 py-1.5 text-xs font-extrabold text-slate-950 shadow-2xs"
            >
              <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
              💰 Waiting RTW Quotes &amp; Cash-Flow
            </button>
            <button
              type="button"
              data-testid="dk-assistant-quick-chip-answers-hub"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('answers_hub');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/70 bg-emerald-700/90 hover:bg-emerald-600 px-3 py-1.5 text-xs font-extrabold text-white shadow-2xs"
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              📚 All AI Answers &amp; Reference Hub ([REF-*])
            </button>
            <button
              type="button"
              onClick={() => {
                setIsOpen(true);
                setActiveTab('memories');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-400/40 bg-indigo-900/60 hover:bg-indigo-800/80 px-3 py-1.5 text-xs font-semibold text-indigo-100"
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              4-Layer Knowledge &amp; Guide ({activeMemoriesCount})
            </button>
            <button
              type="button"
              data-testid="toggle-dk-assistant-drawer-btn"
              onClick={() => setIsOpen((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-3.5 py-1.5 text-xs font-bold text-white shadow-xs"
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              {isOpen ? 'Hide Assistant Panel' : 'Open Unified Co-Pilot'}
            </button>
          </div>
        </div>

        {/* Inline Quick Prompt Input Bar when collapsed */}
        {!isOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!inputText.trim()) return;
              setIsOpen(true);
              setActiveTab('chat');
              void sendMessage(inputText);
            }}
            className="mt-2.5 flex flex-wrap items-center gap-2"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder='Ask or do anything here: "Recreate Synthetic Traveller A #900001 PNR ex-ATH", "Show waiting RTW quotes & cash flow", "Where is X?", or "Remember that..."'
              aria-label="Quick command to DK Management Assistant"
              className="flex-1 min-w-[240px] rounded-lg border border-slate-700 bg-slate-900/90 px-3 py-1.5 text-xs text-white placeholder-slate-400 focus:border-indigo-400 focus:outline-none"
            />
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white"
            >
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              Run Command
            </button>
          </form>
        )}
      </div>

      {/* Expanded Embedded Workspace / Drawer */}
      {isOpen && (
        <div
          data-testid="dk-assistant-expanded-panel"
          className="mt-2 rounded-xl border-2 border-indigo-300 bg-white shadow-lg overflow-hidden"
        >
          {/* Sub-Navigation Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setActiveTab('chat')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'chat'
                    ? 'bg-indigo-950 text-white shadow-2xs'
                    : 'bg-white text-slate-700 hover:bg-slate-200/70'
                }`}
              >
                <Bot className="h-3.5 w-3.5" aria-hidden="true" />
                Assistant Chat &amp; Live Actions
              </button>
              <button
                type="button"
                data-testid="dk-assistant-stokes-sabre-tab"
                onClick={() => setActiveTab('stokes_sabre')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'stokes_sabre'
                    ? 'bg-red-700 text-white shadow-2xs'
                    : 'bg-red-50 text-red-900 border border-red-200 hover:bg-red-100'
                }`}
              >
                <Plane className="h-3.5 w-3.5" aria-hidden="true" />
                 ✈️ Universal Sabre Training Co-Pilot
              </button>
              <button
                type="button"
                data-testid="dk-assistant-answers-hub-tab"
                onClick={() => setActiveTab('answers_hub')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'answers_hub'
                    ? 'bg-emerald-800 text-white shadow-2xs'
                    : 'bg-emerald-50 text-emerald-950 border border-emerald-200 hover:bg-emerald-100'
                }`}
              >
                <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                📚 All AI Answers, Reports &amp; [REF-*] Hub
              </button>
              <button
                type="button"
                data-testid="dk-assistant-cashflow-tab"
                onClick={() => setActiveTab('cashflow_quotes')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'cashflow_quotes'
                    ? 'bg-amber-600 text-white shadow-2xs'
                    : 'bg-amber-50 text-amber-950 border border-amber-200 hover:bg-amber-100'
                }`}
              >
                <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
                💰 Waiting Quotes &amp; PNT Cash-Flow
              </button>
              <button
                type="button"
                data-testid="dk-assistant-memories-tab"
                onClick={() => setActiveTab('memories')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'memories'
                    ? 'bg-indigo-950 text-white shadow-2xs'
                    : 'bg-white text-slate-700 hover:bg-slate-200/70'
                }`}
              >
                <Brain className="h-3.5 w-3.5" aria-hidden="true" />
                4-Layer Knowledge &amp; System Guide ({activeMemoriesCount})
              </button>
              <button
                type="button"
                data-testid="dk-assistant-access-tab"
                onClick={() => setActiveTab('access')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  activeTab === 'access'
                    ? 'bg-indigo-950 text-white shadow-2xs'
                    : 'bg-white text-slate-700 hover:bg-slate-200/70'
                }`}
              >
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                Team Access ({!accessControlReady ? 'Unverified' : accessControl.mode === 'daniel_only' ? 'Daniel Only' : 'Selected Staff'})
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-slate-500">Page Context: {pageContext}</span>
              <button
                type="button"
                onClick={() => setIsFullScreen((prev) => !prev)}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-100"
                title={isFullScreen ? 'Exit full-screen mode' : 'Expand to full-screen single-interface workspace'}
              >
                {isFullScreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                <span>{isFullScreen ? 'Standard Size' : 'Full-Screen Workspace'}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setIsFullScreen(false);
                }}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                title="Close panel"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* TAB 1: CHAT & LIVE ACTIONS */}
          {activeTab === 'chat' && (
            <div className="p-4 space-y-3">
              {/* Pillar B: Global Cross-Client "Ask Propeller" Master AI Copilot Omnichannel Search & Brief */}
              <div
                data-testid="global-ask-propeller-omnichannel-panel"
                className="rounded-xl border border-indigo-300 bg-gradient-to-r from-indigo-50/90 via-white to-sky-50/80 p-3.5 space-y-2.5 shadow-2xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-indigo-950 px-2.5 py-1 text-xs font-extrabold text-white">
                      <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                      Ask Anything Across All Clients (Omnichannel Search &amp; 5-Point Brief)
                    </span>
                    <span className="text-[11px] font-medium text-slate-600">
                      Shortcut: <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">Ctrl/Cmd + J</kbd>
                    </span>
                  </div>
                </div>

                {/* Quick Chips */}
                <div className="flex flex-wrap items-center gap-1.5" data-testid="global-ask-propeller-quick-chips">
                  <button
                    type="button"
                    data-testid="ask-propeller-chip-cruise"
                    onClick={() => void runOmnichannelClientSearch('Cruise customers from the last few days (not Tony Rackley)')}
                    className="rounded-full border border-indigo-200 bg-white hover:bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-950"
                  >
                    🚢 Cruise customers from the last few days (not Tony Rackley)
                  </button>
                  <button
                    type="button"
                    data-testid="ask-propeller-chip-calls-today"
                    onClick={() => void runOmnichannelClientSearch("Today's phone calls & unlinked recordings")}
                    className="rounded-full border border-teal-200 bg-white hover:bg-teal-50 px-2.5 py-1 text-[11px] font-bold text-teal-950"
                  >
                    📞 Today&apos;s phone calls &amp; unlinked recordings
                  </button>
                  <button
                    type="button"
                    data-testid="ask-propeller-chip-next-decision"
                    onClick={() => void runOmnichannelClientSearch('Next ready Decision Card in queue')}
                    className="rounded-full border border-emerald-200 bg-white hover:bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-950"
                  >
                    ✅ Next ready Decision Card in queue
                  </button>
                  <button
                    type="button"
                    data-testid="ask-propeller-chip-booking-forms"
                    onClick={() => void runOmnichannelClientSearch('First Booking Forms ready to process')}
                    className="rounded-full border border-rose-200 bg-white hover:bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-950"
                  >
                    💳 First Booking Forms ready to process
                  </button>
                </div>

                {/* Search Input Bar */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void runOmnichannelClientSearch(omnichannelQuery);
                  }}
                  className="flex flex-wrap items-center gap-2"
                >
                  <input
                    type="text"
                    data-testid="global-ask-propeller-search-input"
                    value={omnichannelQuery}
                    onChange={(e) => setOmnichannelQuery(e.target.value)}
                    placeholder='Search any client, call, WhatsApp, PNR, or topic — e.g. "cruise customer (not Tony Rackley) from a few days ago" or "Glynn Wood"...'
                    className="flex-1 min-w-[240px] rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="global-ask-propeller-search-btn"
                    disabled={omnichannelSearching}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 px-3 py-1.5 text-xs font-extrabold text-white"
                  >
                    <Search className="h-3.5 w-3.5" />
                    <span>{omnichannelSearching ? 'Searching Omnichannel...' : 'Search All Clients'}</span>
                  </button>
                </form>

                {omnichannelExcludedNames.length > 0 && (
                  <div className="text-[11px] font-semibold text-amber-900">
                    Excluded from results: {omnichannelExcludedNames.join(', ')}
                  </div>
                )}

                {/* Matching Client Cards with Inline 5-Point Brief & 1-Click Actions */}
                {omnichannelMatches.length > 0 && (
                  <div
                    data-testid="global-ask-propeller-results-list"
                    className="grid grid-cols-1 gap-2.5 md:grid-cols-2 pt-1 max-h-80 overflow-y-auto"
                  >
                    {omnichannelMatches.map((m) => {
                      const lookupKey = String(m.display_id || m.customer_number || m.primary_pnr || m.customer_id);
                      const loadedBrief = inlineBriefByClient[lookupKey] as {
                        five_point_recap?: {
                          who_and_relationship?: string;
                          did_we_meet_or_speak?: string;
                          the_situation?: string;
                          what_was_discussed_and_promised?: string;
                          what_needs_to_be_done_now?: string;
                        };
                      } | undefined;
                      const inlineDraft = inlineReplyDraftByClient[lookupKey];
                      const inlineStatus = inlineReplyStatusByClient[lookupKey];

                      return (
                        <div
                          key={m.customer_id}
                          data-testid={`omnichannel-client-card-${m.customer_number || m.display_id || m.customer_id}`}
                          className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-xs shadow-2xs"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-1.5">
                            <div className="font-extrabold text-slate-900">
                              {m.customer_name} {m.customer_number ? `(#${m.customer_number})` : ''}
                            </div>
                            <div className="flex items-center gap-1">
                              {m.primary_pnr && (
                                <span className="rounded bg-indigo-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-indigo-950">
                                  1S-{m.primary_pnr}
                                </span>
                              )}
                              {m.match_channel && (
                                <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-900">
                                  {m.match_channel}
                                </span>
                              )}
                            </div>
                          </div>

                          {m.subject && (
                            <div className="text-[11px] font-semibold text-slate-700 truncate">
                              {m.subject}
                            </div>
                          )}

                          {m.match_snippet && (
                            <p className="text-[11px] text-slate-600 line-clamp-2 bg-slate-50 rounded p-1.5 border border-slate-100">
                              “{m.match_snippet}”
                            </p>
                          )}

                          {/* 1-Click Action Buttons on Each Client Card */}
                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            {m.ucc_url && (
                              <Link
                                to={m.ucc_url}
                                className="inline-flex items-center gap-1 rounded bg-indigo-600 hover:bg-indigo-500 px-2.5 py-1 text-[11px] font-bold text-white"
                              >
                                <span>Open Ticket ({m.ucc_url})</span>
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                            <button
                              type="button"
                              onClick={() => void loadInlineClientBrief(lookupKey)}
                              disabled={loadingBriefForClient === lookupKey}
                              className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 text-[11px] font-bold text-indigo-900"
                            >
                              {loadingBriefForClient === lookupKey ? 'Loading Brief...' : 'Load Full 5-Point Brief'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                void loadInlineClientBrief(lookupKey);
                                setInlineReplyStatusByClient((prev) => ({
                                  ...prev,
                                  [lookupKey]: 'Synced 4-way calls & WhatsApp messages onto client ticket.',
                                }));
                              }}
                              className="inline-flex items-center gap-1 rounded border border-teal-200 bg-teal-50 hover:bg-teal-100 px-2 py-1 text-[11px] font-bold text-teal-900"
                            >
                              Sync Call
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (inlineDraft === undefined) {
                                  setInlineReplyDraftByClient((prev) => ({
                                    ...prev,
                                    [lookupKey]: `Hi ${m.customer_name.split(' ')[0] || 'there'},\n\n`,
                                  }));
                                }
                              }}
                              className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-900"
                            >
                              Draft / Send Reply Here
                            </button>
                          </div>

                          {/* Loaded 5-Point Brief Inline */}
                          {loadedBrief?.five_point_recap && (
                            <div className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2 space-y-1 text-[11px]">
                              <div>
                                <strong>1. Who:</strong> {loadedBrief.five_point_recap.who_and_relationship}
                              </div>
                              <div>
                                <strong>2. Spoke/Met:</strong> {loadedBrief.five_point_recap.did_we_meet_or_speak}
                              </div>
                              <div>
                                <strong>3. Situation:</strong> {loadedBrief.five_point_recap.the_situation}
                              </div>
                              <div>
                                <strong>4. Discussed:</strong> {loadedBrief.five_point_recap.what_was_discussed_and_promised}
                              </div>
                              <div>
                                <strong>5. Next Step:</strong> {loadedBrief.five_point_recap.what_needs_to_be_done_now}
                              </div>
                            </div>
                          )}

                          {/* Inline Quick Draft/Send Reply Box */}
                          {inlineDraft !== undefined && (
                            <div className="mt-2 space-y-1.5 border-t border-slate-200 pt-2">
                              <textarea
                                rows={3}
                                value={inlineDraft}
                                onChange={(e) =>
                                  setInlineReplyDraftByClient((prev) => ({ ...prev, [lookupKey]: e.target.value }))
                                }
                                className="w-full rounded border border-slate-300 p-1.5 text-[11px]"
                                placeholder="Type or refine client reply..."
                              />
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] text-slate-500">
                                  Sends via Freshdesk with David Campbell 33-link signature + BCC Daniel
                                </span>
                                {m.ucc_url && (
                                  <Link
                                    to={m.ucc_url}
                                    className="rounded bg-emerald-600 hover:bg-emerald-500 px-2.5 py-1 text-[11px] font-bold text-white"
                                  >
                                    Review &amp; Send in {m.ucc_url} →
                                  </Link>
                                )}
                              </div>
                            </div>
                          )}

                          {inlineStatus && (
                            <div className="text-[11px] font-bold text-emerald-800">{inlineStatus}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Top Pinned Emergency & Cash-Flow Quick Launch Strip */}
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/70 p-2">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-950 mr-1">
                  1-Click Co-Pilot Actions:
                </span>
                <button
                  type="button"
                  onClick={() =>
                    void sendMessage('Are there any First Booking Forms not yet processed?')
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-600 hover:bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-2xs"
                >
                  💳 Uncharged First Booking Forms
                  {questionsQueue && questionsQueue.uncharged_count > 0 ? ` (${questionsQueue.uncharged_count})` : ''}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void sendMessage('What questions and decisions are waiting for me right now?')
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-700 hover:bg-indigo-600 px-2.5 py-1 text-[11px] font-bold text-white shadow-2xs"
                >
                  ❓ Questions Waiting for Daniel
                  {questionsQueue && questionsQueue.open_decisions_count > 0 ? ` (${questionsQueue.open_decisions_count})` : ''}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void sendMessage('Show Waiting RTW Quotes, Cash-In Pipeline & Operator Speed to fund PNT items')
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-500 hover:bg-amber-400 px-2.5 py-1 text-[11px] font-bold text-slate-950 shadow-2xs"
                >
                  💰 Waiting RTW Quotes &amp; PNT Cash-Flow
                </button>
                <button
                  type="button"
                  onClick={() => void sendMessage('What are my must-not-drop emergencies today (Low-Energy Focus)?')}
                  className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-100 hover:bg-purple-200 px-2.5 py-1 text-[11px] font-bold text-purple-950"
                >
                  🛡️ Low-Energy Must-Not-Drop Emergencies
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void sendMessage('Show shared brain standing rules and continuous learning status')
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-950"
                >
                  🧠 Shared Brain Rules ({questionsQueue?.shared_brain_rules_count ?? activeMemoriesCount})
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void sendMessage(
                      'Where is everything in the system and what model & knowledge architecture powers this?'
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white hover:bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-800"
                >
                  🧭 System Guide &amp; Model Architecture
                </button>
              </div>

              {/* Live Bidirectional Questions & Uncharged First Booking Forms Queue */}
              {questionsQueue && (questionsQueue.uncharged_booking_forms.length > 0 || questionsQueue.open_decisions.length > 0) && (
                <div
                  data-testid="dk-assistant-live-questions-and-forms-panel"
                  className="rounded-xl border border-amber-300 bg-amber-50/70 p-3 space-y-2.5 text-xs"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-extrabold text-amber-950 flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                      Live Action Queue — {questionsQueue.uncharged_booking_forms.length} Uncharged First Booking Forms · {questionsQueue.open_decisions.length} Decisions Waiting for Daniel
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Link
                        to="/finance/terminal"
                        className="rounded-md bg-rose-700 hover:bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white"
                      >
                        Open 1-Click Charge Terminal (/finance/terminal)
                      </Link>
                      <Link
                        to="/admin/delivery"
                        className="rounded-md border border-amber-400 bg-white hover:bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-950"
                      >
                        Open Decision Cards (/admin/delivery)
                      </Link>
                    </div>
                  </div>

                  {decisionAnswerNote && (
                    <div role="status" className="rounded border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-bold text-emerald-950">
                      {decisionAnswerNote}
                    </div>
                  )}

                  {questionsQueue.uncharged_booking_forms.length > 0 && (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {questionsQueue.uncharged_booking_forms.slice(0, 4).map((bf) => (
                        <div
                          key={`${bf.ticket_display_id}-${bf.pnr_locator ?? ''}`}
                          className="rounded-lg border border-rose-200 bg-white p-2.5 space-y-1"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-1">
                            <Link
                              to={bf.customer_url}
                              className="font-extrabold text-slate-900 hover:underline"
                            >
                              {bf.customer_name} {bf.customer_number ? `(#${bf.customer_number})` : ''}
                            </Link>
                            <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-900">
                              {bf.card_brand}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-700">{bf.status_reason}</p>
                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <Link
                              to={bf.ucc_url}
                              className="rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-800"
                            >
                              UCC #{bf.ticket_display_id} {bf.fd_ticket_id ? `(FD #${bf.fd_ticket_id})` : ''}
                            </Link>
                            {bf.pnr_url && bf.pnr_locator && (
                              <Link
                                to={bf.pnr_url}
                                className="rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 font-mono text-[10px] font-bold text-indigo-900"
                              >
                                PNR 1S-{bf.pnr_locator}
                              </Link>
                            )}
                            <Link
                              to={bf.terminal_url}
                              className="rounded bg-emerald-700 hover:bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white"
                            >
                              Charge Terminal →
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {questionsQueue.open_decisions.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      {questionsQueue.open_decisions.slice(0, 2).map((dec, dIdx) => (
                        <div
                          key={dec.decision_key}
                          className="rounded-lg border border-indigo-200 bg-white p-2.5 space-y-1.5"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-1">
                            <span className="font-extrabold text-indigo-950">
                              Decision {dIdx + 1} of {questionsQueue.open_decisions.length}: {dec.title}
                            </span>
                            <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-900">
                              {dec.badge}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-700">{dec.ask}</p>
                          {Array.isArray(dec.choices) && dec.choices.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-0.5">
                              {dec.choices.map((ch, cIdx) => (
                                <button
                                  key={cIdx}
                                  type="button"
                                  disabled={answeringDecisionKey === dec.decision_key}
                                  onClick={() => void handleAnswerDecision(dec.decision_key, ch)}
                                  className="rounded-md border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 text-[11px] font-bold text-indigo-950 disabled:opacity-50"
                                >
                                  ✓ {ch}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Message Feed */}
              <div
                className={`${
                  isFullScreen ? 'max-h-[62vh]' : 'max-h-[460px]'
                } overflow-y-auto space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3`}
              >
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 text-xs leading-relaxed ${
                      msg.role === 'user'
                        ? 'ml-8 border border-indigo-200 bg-indigo-50 text-indigo-950'
                        : 'mr-4 border border-slate-200 bg-white text-slate-800 shadow-2xs'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between text-[11px] font-bold text-slate-500">
                      <span className="inline-flex items-center gap-1 text-slate-800">
                        {msg.role === 'user' ? (
                          <UserCheck className="h-3.5 w-3.5 text-indigo-600" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                        )}
                        {msg.sender_name}
                      </span>
                    </div>

                    {/* Executed Actions Strip */}
                    {Array.isArray(msg.executed_actions) && msg.executed_actions.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {msg.executed_actions.map((act, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-bold ${
                              act.blocked_by_guardrail || !act.ok
                                ? 'border-red-300 bg-red-50 text-red-900'
                                : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                            }`}
                          >
                            {act.blocked_by_guardrail || !act.ok ? (
                              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                            )}
                            <span>{act.summary}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {msg.role === 'assistant' ? (
                      <MarkdownMessage className="text-sm text-slate-800">{msg.content}</MarkdownMessage>
                    ) : (
                      <div className="whitespace-pre-line font-sans">{msg.content}</div>
                    )}

                    {/* Interactive 4-Button Sabre PNR Re-Creation Co-Pilot Strip inside Assistant Reply */}
                    {msg.role === 'assistant' && msg.show_stokes_copilot_actions && renderStokesFourActionButtons(true)}

                    {/* Interactive Cash-Flow Quick Jump inside Assistant Reply */}
                    {msg.role === 'assistant' && msg.show_cashflow_actions && (
                      <div className="mt-2.5 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/90 p-2.5">
                        <span className="text-[11px] font-extrabold text-amber-950">
                          Fast Cash-In Actions:
                        </span>
                        <button
                          type="button"
                          onClick={() => setActiveTab('cashflow_quotes')}
                          className="rounded-md bg-amber-600 hover:bg-amber-500 px-2.5 py-1 text-[11px] font-bold text-white"
                        >
                          Open Interactive Waiting Quotes &amp; Turnaround Table →
                        </button>
                        <Link
                          to="/rtw"
                          className="rounded-md border border-indigo-300 bg-white px-2.5 py-1 text-[11px] font-bold text-indigo-900 hover:bg-indigo-50"
                        >
                          Open 60-Sec RTW AI Auto-Builder (/rtw)
                        </Link>
                        <Link
                          to="/pnt"
                          className="rounded-md border border-red-300 bg-white px-2.5 py-1 text-[11px] font-bold text-red-900 hover:bg-red-50"
                        >
                          Open Chronological PNT Queue (/pnt)
                        </Link>
                      </div>
                    )}

                    {/* Suggested Quick Action Chips */}
                    {Array.isArray(msg.suggested_actions) && msg.suggested_actions.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2">
                        {msg.suggested_actions.map((chip, cIdx) => (
                          <button
                            key={cIdx}
                            type="button"
                            onClick={() => void sendMessage(chip.prompt)}
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50/80 hover:bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-900 transition"
                          >
                            <Zap className="h-3 w-3 text-indigo-600" aria-hidden="true" />
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Chat Input Form */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void sendMessage(inputText);
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  data-testid="dk-assistant-chat-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder='Ask or instruct: "Recreate Synthetic Traveller A #900001 PNR ex-ATH", "Show waiting RTW quotes & cash flow", "Where is X?", or "Remember that..."'
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-indigo-600 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="dk-assistant-send-btn"
                  disabled={sending || !inputText.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  {sending ? 'Running…' : 'Send'}
                </button>
              </form>
            </div>
          )}

              {/* TAB 2: INLINE SYNTHETIC SABRE TRAINING CO-PILOT */}
          {activeTab === 'stokes_sabre' && (
            <div className="p-4 space-y-4 text-xs" data-testid="dk-assistant-stokes-sabre-panel">
              {/* 5-Point Memory Recap & Behind-the-Scenes Truth */}
              <div className="rounded-xl border-2 border-red-300 bg-red-50/70 p-3.5 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-extrabold text-red-950 flex items-center gap-2">
                    <span>
                      SYNTHETIC TRAINING — NO LIVE CUSTOMER, PNR OR INVENTORY
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Link
                      to="/rtw"
                      className="rounded-md bg-indigo-700 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-indigo-600"
                    >
                      Open RTW workbench
                    </Link>
                    <Link
                      to="/ops/today"
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800 hover:bg-slate-100"
                    >
                      Open live work board
                    </Link>
                    <Link
                      to="/ucc"
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800 hover:bg-slate-100"
                    >
                      Open UCC
                    </Link>
                    <Link
                      to="/crm"
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800 hover:bg-slate-100"
                    >
                      Open CRM
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 rounded-lg border border-red-200 bg-white p-3">
                  <div>
                    <div className="font-extrabold text-slate-900">1. Who &amp; Relationship + 2. Contact History</div>
                    <p className="text-slate-700 mt-0.5">
                      <strong>Synthetic Traveller A</strong> and <strong>Synthetic Companion</strong> are fictional examples. No contact, payment, loyalty or booking facts on this card are live-loaded. Open the authorised customer record before using operational tools.
                    </p>
                  </div>
                  <div>
                    <div className="font-extrabold text-purple-950">
                      3. Behind-the-Scenes Truth vs Client Story + 4 &amp; 5. Next Action
                    </div>
                    <p className="text-slate-700 mt-0.5">
                      <strong>Training scenario</strong>: a fictional multi-sector booking contains cancelled and waitlisted segments. <strong>Action</strong>: locate the authorised live record, refresh it, and verify every segment before proposing or executing a change.
                    </p>
                  </div>
                </div>
              </div>

              {/* 4 Interactive Co-Pilot Action Buttons */}
              {renderStokesFourActionButtons(false)}

              {/* 🎯 Oneworld Codeshare & Customer Tier Point Maximiser Panel */}
              <div
                data-testid="codeshare-maximiser-panel"
                className="rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50/90 via-white to-indigo-50/80 p-3.5 space-y-2.5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-950 flex items-center gap-1.5">
                      <Zap className="h-4 w-4 text-emerald-700" />
                      🎯 Synthetic Oneworld Codeshare &amp; Tier Target Training
                    </div>
                    <p className="text-[11px] text-slate-700 mt-0.5">
                      Automatically scans every leg for `BA*`, `AA*`, `AY*`, `JL*`, and `QF*` codeshares that maximise Tier Points, Qualifying Flights, and D/A bucket availability for each client&apos;s loyalty target.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="toggle-codeshare-maximiser-btn"
                    onClick={() => setCodeshareMaximiserEnabled((v) => !v)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-extrabold transition ${
                      codeshareMaximiserEnabled
                        ? 'border-emerald-700 bg-emerald-700 text-white shadow-2xs'
                        : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-100'
                    }`}
                  >
                    {codeshareMaximiserEnabled
                      ? '✓ Codeshare Maximiser ON (20,464 BA TP · Clears 20k Gold · 6 BA QFs)'
                      : 'Enable Codeshare Maximiser (+6,642 TP & +4 BA Qualifying Flights)'}
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {CUSTOMER_CODESHARE_MAXIMISER_TARGETS.map((c) => (
                    <div
                      key={c.customerNumber}
                      className="rounded-lg border border-emerald-200 bg-white p-2.5 space-y-1 text-[11px]"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-1">
                        <Link
                          to={`/crm/customers/${c.customerNumber}`}
                          className="font-extrabold text-indigo-950 hover:underline"
                        >
                          {c.customerName} (#{c.customerNumber})
                        </Link>
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-950">
                          {c.codeshareMaximisedPoints}
                        </span>
                      </div>
                      <div className="text-slate-600">
                        <strong>Target:</strong> {c.programAndGoal} (Prime baseline: {c.currentBaselinePoints})
                      </div>
                      <div className="text-emerald-950">
                        <strong>Recommended Codeshare Swaps:</strong> {c.keyCodeshareSwaps}
                      </div>
                      <div className="font-mono text-[10px] text-indigo-800">
                        <strong>Sabre Pricing Check:</strong> {c.sabreCheckCommand}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 13-Segment ex-ATH Re-Originated DONE5 Table */}
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-900 px-3.5 py-2.5 text-white">
                  <div className="font-bold">
                    13-Segment ex-ATH Re-Originated DONE5 Recovery Plan (Starts 24 Oct 2026 · Closes 18 Feb 2027 in ATH per Rule 3015)
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setQf62UseJlAlternative((v) => !v)}
                      className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition ${
                        qf62UseJlAlternative
                          ? 'border-emerald-400 bg-emerald-600 text-white'
                          : 'border-amber-400 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                      }`}
                    >
                      {qf62UseJlAlternative
                        ? '✓ Leg #7 Swapped to Confirmed JL 771 NRT→SYD + BA*7412/QF 512 SYD→BNE (+2,057 TP)'
                        : 'Swap Waitlisted Leg #7 (QF 62) → Confirmed JL 771 NRT→SYD + BA*7412 SYD→BNE'}
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-100 text-[11px] font-extrabold uppercase text-slate-700">
                        <th className="py-2 px-2.5">#</th>
                        <th className="py-2 px-2.5">Date</th>
                        <th className="py-2 px-2.5">Prime Flight</th>
                        <th className="py-2 px-2.5">Recommended Codeshare (Target Maximiser)</th>
                        <th className="py-2 px-2.5">Route &amp; Times</th>
                        <th className="py-2 px-2.5">Old DEMOAA</th>
                        <th className="py-2 px-2.5">Illustrative Sabre Action</th>
                        <th className="py-2 px-2.5">Sabre Cryptic Sell</th>
                        <th className="py-2 px-2.5 text-right">BA TP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {STOKES_EX_ATH_13_SEGMENTS.map((seg) => {
                        const isSwappedQf62 = seg.index === 7 && qf62UseJlAlternative;
                        const activeSell = codeshareMaximiserEnabled
                          ? seg.codeshareSabreSell
                          : isSwappedQf62
                            ? '02D104DECNRTSYD¤JL771 + 02D105DECSYDBNE¤QF512'
                            : seg.sabreSell;
                        const activeTp =
                          codeshareMaximiserEnabled || isSwappedQf62 ? seg.codeshareTp : seg.tp;
                        return (
                          <tr
                            key={seg.index}
                            className={
                              seg.oldKmukpoStatus.includes('HX')
                                ? 'bg-red-50/40'
                                : seg.oldKmukpoStatus.includes('HL')
                                  ? 'bg-amber-50/60'
                                  : seg.oldKmukpoStatus.includes('NEW')
                                    ? 'bg-emerald-50/50'
                                    : 'bg-white'
                            }
                          >
                            <td className="py-2 px-2.5 font-bold text-slate-900">{seg.index}</td>
                            <td className="py-2 px-2.5 font-semibold text-slate-900 whitespace-nowrap">
                              {seg.dateLabel}
                            </td>
                            <td className="py-2 px-2.5 font-mono font-bold text-slate-700">
                              {seg.flight} ({seg.bookingClass})
                            </td>
                            <td className="py-2 px-2.5">
                              <div className="font-mono font-bold text-indigo-950">
                                {seg.recommendedCodeshareFlight}
                              </div>
                              <div className="text-[10px] text-emerald-800 font-semibold">
                                {seg.codeshareBenefit}
                              </div>
                            </td>
                            <td className="py-2 px-2.5 font-semibold text-slate-900">
                              {isSwappedQf62 ? 'NRT → SYD → BNE' : `${seg.from} → ${seg.to}`}{' '}
                              <span className="text-slate-500 font-normal">({seg.depArr})</span>
                            </td>
                            <td className="py-2 px-2.5">
                              <span
                                className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                  seg.oldKmukpoStatus.includes('HX')
                                    ? 'bg-red-100 text-red-900'
                                    : seg.oldKmukpoStatus.includes('HL')
                                      ? 'bg-amber-100 text-amber-900'
                                      : seg.oldKmukpoStatus.includes('NEW')
                                        ? 'bg-indigo-100 text-indigo-900'
                                        : 'bg-emerald-100 text-emerald-900'
                                }`}
                              >
                                {seg.oldKmukpoStatus}
                              </span>
                            </td>
                            <td className="py-2 px-2.5 font-semibold text-emerald-900">
                              <div>
                                {isSwappedQf62
                                  ? 'Synthetic alternative via SYD — verify before use'
                                  : seg.liveStatusDefault}
                              </div>
                              <div className="text-[10px] text-indigo-700 font-bold">{seg.uiRuleStatus}</div>
                            </td>
                            <td className="py-2 px-2.5 font-mono text-[11px] text-slate-800 whitespace-pre-line">
                              {activeSell}
                            </td>
                            <td className="py-2 px-2.5 text-right font-bold text-slate-900">
                              {activeTp}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ready-to-Paste Sabre Cryptic Block */}
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-3.5 text-slate-100 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
                    <Terminal className="h-4 w-4" />
                    Ready-to-Paste Sabre Cryptic Block (Inventory PCC: BN5K · Shell PCC: DW9L · 2 PAX)
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleCopySabreBlock()}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 px-3 py-1 text-xs font-extrabold text-slate-950"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {copiedCryptic ? 'Copied to Clipboard!' : 'Copy Full Sabre Cryptic Block'}
                  </button>
                </div>
                <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 font-mono text-xs text-emerald-300 leading-relaxed">
                  {qf62UseJlAlternative
                    ? STOKES_EX_ATH_SABRE_CRYPTIC_BLOCK.replace(
                        '02D104DECNRTBNE¤QF62',
                        '02D104DECNRTSYD¤JL771\n02D105DECSYDBNE¤QF512'
                      )
                    : STOKES_EX_ATH_SABRE_CRYPTIC_BLOCK}
                </pre>
              </div>

              {/* Universal Reality-Check, 3 Sabre Rescue Options (Fri 23 / Sat 24 Oct) & 5 Peer Questions */}
              <RealityCheckCopilotPanel
                initialSubTab="sabre_rescue"
                onSendContinuePromptToChat={(prompt) => {
                  setActiveTab('chat');
                  void sendMessage(prompt);
                }}
              />
            </div>
          )}

          {/* TAB 2B: ALL AI ANSWERS, REPORTS & FOLLOW-UP REFERENCE HUB ([REF-*]) */}
          {activeTab === 'answers_hub' && (
            <div className="p-4" data-testid="dk-assistant-answers-hub-panel">
              <RealityCheckCopilotPanel
                initialSubTab="answers_hub"
                onSendContinuePromptToChat={(prompt) => {
                  setActiveTab('chat');
                  void sendMessage(prompt);
                }}
              />
            </div>
          )}

          {/* TAB 3: WAITING RTW QUOTES & CASH-FLOW ENGINE */}
          {activeTab === 'cashflow_quotes' && (
            <div className="p-4 space-y-4 text-xs" data-testid="dk-assistant-cashflow-panel">
              {/* Top Diagnostic Banner: Operator Speed vs AI Auto-Builder + Cash-In Pipeline */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/80 p-3.5 space-y-1">
                  <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-900">
                    1. Active RTW Quote Cash-In Pipeline
                  </div>
                  <div className="text-xl font-black text-emerald-950">synthetic pipeline</div>
                  <p className="text-slate-700">
                    6 high-probability RTW quotes &amp; consultations ready to close to generate immediate cash flow for PNT ticket issuance.
                  </p>
                </div>

                <div className="rounded-xl border-2 border-amber-300 bg-amber-50/80 p-3.5 space-y-1">
                  <div className="text-[11px] font-extrabold uppercase tracking-wider text-amber-950">
                    2. Operator Quote Turnaround Bottleneck
                  </div>
                  <div className="text-xl font-black text-amber-950">4 Hours Manual → 15 Mins AI</div>
                  <p className="text-slate-700">
                    Manual Sabre segment search + carrier YQ testing takes ~3.5–4.5h/quote. Pre-building in{' '}
                    <Link to="/rtw" className="font-bold underline text-indigo-900">
                      /rtw (AI Auto-Builder)
                    </Link>{' '}
                    takes <strong>60 seconds</strong> so the operator finishes in <strong>15 minutes</strong> (16× throughput).
                  </p>
                </div>

                <div className="rounded-xl border-2 border-purple-300 bg-purple-50/80 p-3.5 space-y-1">
                  <div className="text-[11px] font-extrabold uppercase tracking-wider text-purple-950">
                    3. Chronological PNT Issuance Queue Funded
                  </div>
                  <div className="text-xl font-black text-purple-950">24 Oct → 13 Nov Window</div>
                  <p className="text-slate-700">
                    This synthetic pipeline illustrates chronological PNT prioritisation. Use live quote, payment and departure evidence before making a cash-flow or ticketing decision.
                  </p>
                </div>
              </div>

              {/* Waiting RTW Quotes Table */}
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-900 px-3.5 py-2.5 text-white">
                  <span className="font-bold">
                    Hot RTW Quotes &amp; Clients Waiting to Close / Pay Right Now (Sorted by Fastest Cash-In)
                  </span>
                  <Link
                    to="/rtw"
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1 text-[11px] font-extrabold text-white"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Open 60-Second RTW AI Auto-Builder (/rtw)
                  </Link>
                </div>

                <div className="divide-y divide-slate-200">
                  {WAITING_RTW_QUOTES_PIPELINE.map((q) => (
                    <div key={q.rank} className="p-3.5 hover:bg-slate-50/80 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-md bg-slate-900 px-2 py-0.5 text-[11px] font-extrabold text-white">
                            #{q.rank}
                          </span>
                          <Link
                            to={`/crm/customers/${q.customerNumber}`}
                            className="text-sm font-extrabold text-indigo-950 hover:underline"
                          >
                            {q.customerName} (#{q.customerNumber})
                          </Link>
                          <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-extrabold text-emerald-950">
                            {q.estCashInDisplay}
                          </span>
                          <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-900">
                            {q.waitDuration}
                          </span>
                          {q.pnrLocator && (
                            <Link
                              to={`/pnr/${q.pnrLocator.toLowerCase()}`}
                              className="rounded border border-slate-300 bg-white px-2 py-0.5 font-mono text-[11px] font-bold text-slate-800 hover:bg-slate-100"
                            >
                              PNR {q.pnrLocator}
                            </Link>
                          )}
                          {q.uccId && (
                            <Link
                              to={`/ucc/${q.uccId}`}
                              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-bold text-slate-800 hover:bg-slate-100"
                            >
                              UCC #{q.uccId}
                            </Link>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setActiveTab('chat');
                              void sendMessage(q.delegatePrompt);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-indigo-700 hover:bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white"
                          >
                            <Zap className="h-3.5 w-3.5" />
                            Fast-Track / Delegate to Operator (15-Min Close)
                          </button>
                          <Link
                            to="/rtw"
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-800"
                          >
                            Pre-Build in /rtw
                          </Link>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
                        <span className="font-bold text-slate-900">5-Point Recap: </span>
                        {q.fivePointRecapSummary}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                        <span className="text-red-800">
                          <strong>Current Bottleneck:</strong> {q.bottleneck}
                        </span>
                        <span className="text-emerald-900 font-bold">
                          <strong>15-Min Action:</strong> {q.fastCloseAction}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: 4-LAYER LEARNED KNOWLEDGE, MODEL ARCHITECTURE & SYSTEM GUIDE */}
          {activeTab === 'memories' && (
            <div className="p-4 space-y-4 text-xs">
              {/* Model & 4-Layer Knowledge Architecture Explainer Card */}
              <div
                data-testid="dk-assistant-knowledge-architecture-card"
                className="rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-purple-50 p-4 space-y-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-extrabold text-indigo-950 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-indigo-700" />
                    <span>How This Co-Pilot Works: AI Model Selection &amp; 4-Layer Knowledge Management</span>
                  </div>
                  <span className="rounded-full bg-indigo-950 px-3 py-0.5 text-[11px] font-bold text-amber-300">
                    Provider and retention policy are not reported by this response
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border border-indigo-200 bg-white p-2.5 space-y-1">
                    <div className="font-extrabold text-indigo-950">Layer 1: Standing Rules &amp; Learned Memory</div>
                    <p className="text-slate-600">
                      Saved rules remain available for review after a confirmed receipt and readback. Saving a rule does not confirm that another workflow has applied it.
                    </p>
                  </div>
                  <div className="rounded-lg border border-purple-200 bg-white p-2.5 space-y-1">
                    <div className="font-extrabold text-purple-950">Layer 2: Two-Track Behind-the-Scenes Vault</div>
                    <p className="text-slate-600">
                      Stored in `narrative_vs_reality`. Separates <strong>Track A (internal operational evidence)</strong> from <strong>Track B (customer-facing narrative)</strong> so internal notes are handled deliberately.
                    </p>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-white p-2.5 space-y-1">
                    <div className="font-extrabold text-emerald-950">Layer 3: Live Sabre, PNT &amp; RTW Draft Context</div>
                    <p className="text-slate-600">
                      Use the selected live case for segment status, payment evidence and saved RTW drafts. Training examples do not establish current booking status or availability.
                    </p>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-white p-2.5 space-y-1">
                    <div className="font-extrabold text-amber-950">Layer 4: Inline System Navigator &amp; Executor</div>
                    <p className="text-slate-600">
                      Opens the relevant workflow and offers scoped staff actions. Training controls do not execute Sabre commands, create bookings or confirm live availability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Interactive System Navigator ("Where is X in the system?") */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-2.5">
                <div className="font-extrabold text-slate-900 flex items-center gap-1.5">
                  <Compass className="h-4 w-4 text-indigo-700" />
                  <span>Interactive System Navigator — Where Everything Is in the Platform (1-Click Jump)</span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {SYSTEM_NAVIGATION_DIRECTORY.map((entry) => (
                    <Link
                      key={entry.path}
                      to={entry.path}
                      className="group rounded-lg border border-slate-200 bg-white p-2.5 hover:border-indigo-400 hover:shadow-xs transition flex flex-col justify-between"
                    >
                      <div>
                        <div className="font-bold text-indigo-950 group-hover:text-indigo-700 flex items-center justify-between gap-1">
                          <span>{entry.area}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-indigo-600" />
                        </div>
                        <div className="font-mono text-[10px] font-bold text-indigo-600 mt-0.5">{entry.path}</div>
                        <p className="text-[11px] text-slate-600 mt-1">{entry.description}</p>
                      </div>
                      <div className="mt-2 pt-1.5 border-t border-slate-100 text-[10px] font-semibold text-emerald-900">
                        {entry.whatYouCanDoThere}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Teach New Standing Rule or Behind-the-Scenes Truth Form */}
              {contextLoadError && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-950">{contextLoadError}</p>}
              {memorySaveNotice && <p role="status" className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-xs font-semibold text-slate-900">{memorySaveNotice}</p>}
              <form
                onSubmit={handleSaveNewMemory}
                className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2.5"
              >
                <div className="text-xs font-extrabold uppercase tracking-wide text-indigo-950 flex items-center gap-1.5">
                  <Plus className="h-4 w-4 text-indigo-600" />
                  Propose a Standing Rule, Case Truth, or Routing Preference
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <input
                    type="text"
                    value={newMemoryTitle}
                    onChange={(e) => setNewMemoryTitle(e.target.value)}
                    placeholder="Short title for the source-backed rule"
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
                  />
                  <select
                    value={newMemoryCategory}
                    onChange={(e) => setNewMemoryCategory(e.target.value)}
                    aria-label="Standing rule category"
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
                  >
                    <option value="case_truth">Behind-the-Scenes Case Truth</option>
                    <option value="priority_rule">Priority Rule</option>
                    <option value="routing_preference">Routing Preference</option>
                    <option value="team_boundary">Team Boundary / Guardrail</option>
                    <option value="communication_guardrail">Communication Guardrail</option>
                    <option value="workflow_instruction">Workflow Instruction</option>
                    <option value="client_preference">Client Preference</option>
                  </select>
                  <input
                    type="text"
                    value={newMemoryStaff}
                    onChange={(e) => setNewMemoryStaff(e.target.value)}
                    placeholder="Applies to (e.g. All, RTW Specialist, Gebie)"
                    className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    required
                    value={newMemoryText}
                    onChange={(e) => setNewMemoryText(e.target.value)}
                    placeholder="Full source-backed instruction; saved only after receipt and readback"
                    className="flex-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
                  />
                  <button
                    type="submit"
                    disabled={savingMemory}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-700 hover:bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {savingMemory ? 'Saving…' : 'Save Rule'}
                  </button>
                </div>
              </form>

              <div className="space-y-2">
                {memories.map((mem) => (
                  <div
                    key={mem.id}
                    className={`flex flex-wrap items-start justify-between gap-3 rounded-lg border p-3 text-xs ${
                      mem.is_active
                        ? 'border-slate-200 bg-white'
                        : 'border-slate-200 bg-slate-100 opacity-60'
                    }`}
                  >
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-slate-900">{mem.title}</span>
                        <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-900">
                          {mem.category.replace(/_/g, ' ')}
                        </span>
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                          Staff: {(mem.applies_to_staff || ['All']).join(', ')}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          Applied {mem.times_applied}×
                        </span>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{mem.instruction_text}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleToggleMemory(mem)}
                      className={`rounded-md border px-2.5 py-1 text-[11px] font-bold ${
                        mem.is_active
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                          : 'border-slate-300 bg-white text-slate-600'
                      }`}
                    >
                      {mem.is_active ? 'Active' : 'Paused'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: TEAM ACCESS & ROLLOUT CONTROLS */}
          {activeTab === 'access' && (
            <div className="p-4 space-y-3 text-xs">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3.5 space-y-3">
                <div className="font-bold text-slate-900 text-sm">
                  DK Management Assistant &amp; Consultation Briefs Visibility
                </div>
                <p className="text-slate-600">
                  An administrator selects approved staff accounts by immutable user ID. Display names in older settings do not grant access. Saving the list does not enable selected-staff mode; the default remains Daniel Only.
                </p>
                {memorySaveNotice && <p role="status" className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-slate-900">{memorySaveNotice}</p>}
                {!accessControlReady && <p role="status" className="text-amber-900">Current access settings are unavailable or still loading. Mode changes are paused.</p>}
                {selectedActorLoadError && <p role="alert" className="text-amber-900">{selectedActorLoadError}</p>}
                {!selectedActorCatalog && !selectedActorLoadError && <p role="status">Checking selected staff access…</p>}
                {selectedActorCatalog && (
                  <div className="space-y-2">
                    <div className="font-semibold text-slate-800">Approved staff accounts</div>
                    {selectedActorCatalog.eligible_staff.length === 0 && <p>No approved staff accounts are available to select.</p>}
                    {selectedActorCatalog.selected_user_ids.some((id) => !selectedActorCatalog.eligible_staff.some((staff) => staff.user_id === id)) &&
                      <p role="status" className="text-amber-900">A previously selected account is no longer approved or available. Saving this list will remove it.</p>}
                    <div className="max-h-44 overflow-y-auto space-y-1">
                      {selectedActorCatalog.eligible_staff.map((staff) => (
                        <label key={staff.user_id} className="flex items-start gap-2 rounded border border-slate-200 bg-white p-2">
                          <input
                            type="checkbox"
                            checked={selectedActorDraft.includes(staff.user_id)}
                            onChange={(event) => setSelectedActorDraft((current) => event.target.checked
                              ? [...current, staff.user_id]
                              : current.filter((id) => id !== staff.user_id))}
                          />
                          <span>{staff.display_name} ({staff.role}) <span className="block font-mono text-[10px] text-slate-600">{staff.user_id}</span></span>
                        </label>
                      ))}
                    </div>
                    <button type="button" onClick={() => void handleSaveSelectedActors()} className="rounded-lg border border-blue-700 bg-blue-700 px-3 py-2 font-bold text-white">
                      Save selected staff IDs
                    </button>
                    <p className="text-slate-600">Only accounts explicitly saved here can use selected-staff mode; current approval and customer permissions are still checked for every request.</p>
                  </div>
                )}
                {selectedActorCatalog && <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void handleUpdateAccessMode('daniel_only')}
                    disabled={!accessControlReady}
                    className={`rounded-lg border px-3.5 py-2 font-bold ${
                      accessControl.mode === 'daniel_only'
                        ? 'border-indigo-600 bg-indigo-950 text-white'
                        : 'border-slate-300 bg-white text-slate-800'
                    }`}
                  >
                    🔒 Daniel Only (Default Founder Mode)
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleUpdateAccessMode('selected_staff')}
                    disabled={!accessControlReady || !hasEligibleSelectedActor}
                    className={`rounded-lg border px-3.5 py-2 font-bold ${
                      accessControl.mode === 'selected_staff'
                        ? 'border-indigo-600 bg-indigo-950 text-white'
                        : 'border-slate-300 bg-white text-slate-800'
                    }`}
                  >
                    👥 Enable explicitly selected staff
                  </button>
                </div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DkManagementAssistantDrawer;
